import { INotSure } from "@/types";
import AppNotification from "@/utils/AppNotification";
import axios from "axios";
import axiosInstance from "./AxiosInstance";
import { clientToken } from "./Constant";
import { getValue } from "./Storage";
import { ContentType, MethodTypeEnum } from "@/enums";

/**
 * @function AppRequest
 * @author traj3ctory
 * @param {string} url i.e "product/${id}"
 * @param {string} method i.e POST
 * @param {string} data {name: 'John', age: 47}
 * @return {promise}
 */

const _baseTimeOut = import.meta.env.VITE_REACT_APP_API_TIMEOUT || 50_000;

export const AppRequest = async <T = INotSure>(
  url: string,
  method: MethodTypeEnum = MethodTypeEnum.GET,
  data?: INotSure,
  useToken = true,
  timeout = _baseTimeOut,
  contentType?: ContentType,
  signal?: AbortSignal
): Promise<T> => {

  const token = getValue(clientToken);
  if (useToken && !token) {
    setTimeout(() => {
      AppNotification.error({
        msg: "Attention",
        desc: "Login session expired, please login again",
      });
    }, 3000);
    
    return Promise.reject("Token not found");
  }

  // check if there is internet connection, if not, return an error
  if (!navigator.onLine) {
    AppNotification.error({
      msg: "Error",
      desc: "No internet connection",
    });
    throw "No internet connection";
  }

  const config = {
    method,
    url,
    data,
    headers: {
      "Content-Type": contentType || ContentType.JSON,
    },
    signal,
    timeout,
  };

  try {
    const response = await axiosInstance.request(config);
    return response?.data as T;
  } catch (error: INotSure) {
    // Handle request cancellation

    if (axios.isCancel(error)) {
      return Promise.reject("Request canceled");
    }

    const errorMessage =
      error?.response?.data?.message || error?.message || error;

    AppNotification.error({
      msg: "Error",
      desc: errorMessage,
    });
    throw errorMessage;
  }
};

export default Request;
